import React, { useEffect, useState } from 'react'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import BlockTitle from './BlockTitle'
import { AllStepsView } from './AllStepsView'
import OnboardingDrawer from '../components/OnboardingDrawer'
import { useOnboardingContext } from '../context/OnboardingContext'
import LaunchConfirmModal from '../components/drawerContents/launchCampaignComponents/LaunchConfirmModal'
import { OnboardingActionsProvider } from '../context/OnboardingActionsContext'
import { useNavigate } from 'react-router-dom'
import {
  OnboardingStepStatus,
  useGetOnboardingStepsQuery,
  useSendSimulationCampaignMutation
} from '../../../api/onboardingApiSlice'
import useMutation from '../../../hooks/useMutation'
import ArrowCollapseButton from './ArrowCollapseButton'

export default function OnboardingBlock() {
  const {
    allCards,
    activeDrawerType,
    openDrawer,
    closeDrawer,
    handleCompleteStep,
    currentStepDetails,
    OnboardingStepType
  } = useOnboardingContext()
  const navigate = useNavigate()
  const stepIsInProgress = allCards?.find(card => card.status === OnboardingStepStatus.IN_PROGRESS)
  const [previewStepId, setPreviewStepId] = useState(null)
  const [collapsed, setCollapsed] = useState(true)
  const [launchConfirmModalOpen, setLaunchConfirmModalOpen] = useState(false)
  const [campaignModalData, setCampaignModalData] = useState(null)
  const { data: onboardingSteps, isLoading } = useGetOnboardingStepsQuery()
  const { submit } = useMutation(useSendSimulationCampaignMutation, {
    successMessage: 'Campaign launched successfully',
    onSuccess: closeDrawer
  })
  const activeDrawerTitle = allCards.find(
    card => card.drawerType === activeDrawerType || card.secondaryActionDrawerType === activeDrawerType
  )?.title

  const completedStepsCount =
    onboardingSteps?.filter(step => step.status === OnboardingStepStatus.COMPLETED).length || 0
  const totalStepsCount = onboardingSteps?.length || 0
  const isOnboardingComplete = completedStepsCount === totalStepsCount

  const handleStepCompletionForBlock = (stepApiTypeOfDrawer, payload) => {
    if (
      currentStepDetails?.apiType === OnboardingStepType.RUN_PHISHING_DRILL &&
      payload?.status === 'success' &&
      payload?.modalData
    ) {
      setCampaignModalData(payload.modalData)
      setLaunchConfirmModalOpen(true)
      submit(payload.modalData)
    } else {
      if (handleCompleteStep && currentStepDetails?.apiType) {
        handleCompleteStep(currentStepDetails.apiType, payload?.intent === 'skip')
      }
    }
  }

  const handleCloseConfirmModal = () => {
    setLaunchConfirmModalOpen(false)
    setCampaignModalData(null)
  }

  const handleViewCampaignDetails = () => {
    setLaunchConfirmModalOpen(false)
    navigate(`/simulation-campaign`)
    setCampaignModalData(null)
  }

  const handleToggleCollapse = () => setCollapsed(v => !v)

  const onboardingActionsContextValue = {
    handleCompleteStep,
    OnboardingStepType,
    allSteps: allCards,
    currentStepDetails
  }

  useEffect(() => {
    if (isOnboardingComplete) {
      setCollapsed(true)
    } else {
      setCollapsed(false)
    }
  }, [isOnboardingComplete])

  useEffect(() => {
    if (stepIsInProgress) {
      setPreviewStepId(stepIsInProgress.id)
    } else {
      setPreviewStepId(null)
    }
  }, [stepIsInProgress])

  if (isLoading) {
    return <SkeletonWrapper />
  }

  return (
    <OnboardingActionsProvider value={onboardingActionsContextValue}>
      <div>
        <Stack direction={'row'} columnGap={2} alignItems={'center'} pb={2}>
          <BlockTitle title={'First Steps'} />
          <Box sx={counterWrapper}>
            {isOnboardingComplete ? (
              <Typography sx={badgeSx}>Completed</Typography>
            ) : (
              <Typography sx={badgeSx}>
                {completedStepsCount} of {totalStepsCount}
              </Typography>
            )}
          </Box>
          {isOnboardingComplete && <ArrowCollapseButton expanded={!collapsed} onClick={handleToggleCollapse} />}
        </Stack>
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
            transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            maxHeight: collapsed ? 0 : 2000,
            opacity: collapsed ? 0 : 1,
            transitionProperty: 'max-height, opacity',
            transitionDuration: '0.5s'
          }}
        >
          <AllStepsView
            cards={allCards}
            onSelectStep={setPreviewStepId}
            activeStepId={previewStepId}
            onOpenDrawer={openDrawer}
            onCompleteStep={handleCompleteStep}
          />
          <OnboardingDrawer
            open={!!activeDrawerType}
            onClose={closeDrawer}
            drawerType={activeDrawerType}
            title={activeDrawerTitle || 'Step Details'}
            currentStepDetails={currentStepDetails}
            completeStepAndProceed={handleStepCompletionForBlock}
          />
          <LaunchConfirmModal
            isOpen={launchConfirmModalOpen}
            onClose={handleCloseConfirmModal}
            campaignName={campaignModalData?.campaignName}
            onViewDetails={handleViewCampaignDetails}
          />
        </Box>
      </div>
    </OnboardingActionsProvider>
  )
}
const counterWrapper = {
  width: 'auto',
  minWidth: '65px',
  background: '#E0E9FF',
  borderRadius: '1000px',
  padding: '6px 12px'
}
const badgeSx = {
  display: 'flex',
  justifyContent: 'center',
  fontFamily: 'Plus Jakarta Sans',
  fontWeight: 700,
  fontSize: '14px',
  color: '#0246F8'
}

const SkeletonWrapper = () => {
  return (
    <div>
      <Stack direction={'row'} columnGap={2} alignItems={'center'} pb={2}>
        <Skeleton variant='text' width={120} height={30} />
        <Skeleton variant='rounded' width={65} height={32} sx={{ borderRadius: '1000px' }} />
      </Stack>
      <Box sx={{ width: '100%' }}>
        <Stack spacing={2}>
          {[1, 2, 3].map(item => (
            <Skeleton key={item} variant='rounded' height={120} sx={{ borderRadius: '12px' }} />
          ))}
        </Stack>
      </Box>
    </div>
  )
}
